import React, { useEffect, useRef, useState } from "react";
import { rangeLabel } from "util/manualDeliveryTime";

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
// A whole, real calendar date. A native date field reports "0002-10-01" while a year is still being typed, and
// that must not load anything.
const fullDate = (value) => {
  const match = ISO_DATE.exec(value || "");
  if (!match || Number(match[1]) < 2000) return false;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

// "Oct 7 – Nov 6": opens two native date fields that apply as soon as both are valid (no Apply button). Dates are
// the shop's calendar dates (YYYY-MM-DD).
export default function DateRangeButton({ range, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(range);
  const [error, setError] = useState("");
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const firstRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    if (firstRef.current) firstRef.current.focus();
    const onPointer = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open]);

  const show = () => {
    setDraft(range);
    setError("");
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    if (buttonRef.current) buttonRef.current.focus();
  };
  const change = (key) => (event) => {
    const next = { ...draft, [key]: event.target.value };
    setDraft(next);
    if (!next.from || !next.to) {
      setError("Pick both dates");
    } else if (!fullDate(next.from) || !fullDate(next.to)) {
      // Still typing: wait for a whole date.
      setError("");
    } else if (next.to < next.from) {
      setError("The end date is before the start date");
    } else {
      setError("");
      onChange(next);
    }
  };
  const invalid = error ? "true" : undefined;

  return (
    <div
      ref={wrapRef}
      className="md-range"
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          event.stopPropagation();
          close();
        }
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        className="md-btn"
        aria-haspopup="dialog"
        aria-expanded={open ? "true" : "false"}
        onClick={() => (open ? close() : show())}
      >
        <span className="md-visually-hidden">Date range: </span>
        {rangeLabel(range.from, range.to)}
      </button>
      {open ? (
        <div role="dialog" aria-label="Date range" className="md-range__pop">
          <div className="md-range__row">
            <label className="md-field">
              From
              <input
                ref={firstRef}
                type="date"
                className="md-input"
                value={draft.from}
                aria-invalid={invalid}
                aria-describedby={error ? "md-range-error" : undefined}
                onChange={change("from")}
              />
            </label>
            <label className="md-field">
              To
              <input
                type="date"
                className="md-input"
                value={draft.to}
                min={draft.from}
                aria-invalid={invalid}
                aria-describedby={error ? "md-range-error" : undefined}
                onChange={change("to")}
              />
            </label>
          </div>
          {error ? (
            <span id="md-range-error" className="md-field__error" role="alert">
              {error}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
